// Grades how far a run's input data lags the run itself. Pure; the banner
// that renders it lives in staleness_banner.tsx.
//
// The gap measured is engine_manifest.today_proxy (where actuals stop)
// against the run's OWN execution date. Deliberately NOT wall clock:
// measuring against "now" would fire on every historical run someone opens,
// which is the normal way to browse this page.

/** Silent below this: normal pipeline lag, not worth a word. */
export const NOTE_AFTER_DAYS = 7;
/** Above this the gap is no longer plausibly routine. */
export const PROMINENT_AFTER_DAYS = 21;

export type StalenessLevel = "silent" | "note" | "prominent";

export type Staleness = {
  level: StalenessLevel;
  /** Whole days between the data boundary and the run's execution. */
  days: number;
};

const DAY_MS = 86_400_000;

/**
 * Grades the gap between a run's data boundary and its execution date.
 *
 * Returns "silent" whenever it cannot tell — a missing manifest (older runs
 * predate it) must not produce a warning, because "we don't know" and "the
 * data is stale" are different claims and only one of them belongs on screen.
 * A negative gap (boundary after execution) is also silent: it means the
 * engine was given a forward-dated proxy, which is a different thing than
 * staleness and would only confuse the reader here.
 */
export const assessStaleness = ({
  todayProxyMs,
  runCreatedAtMs,
}: {
  todayProxyMs: number | null;
  runCreatedAtMs: number | null;
}): Staleness => {
  if (todayProxyMs == null || runCreatedAtMs == null) {
    return { level: "silent", days: 0 };
  }
  // Truncate both ends to a UTC day before differencing, so a run that
  // executed a few hours after its boundary reads as 0 days rather than 1.
  const boundaryDay = Math.floor(todayProxyMs / DAY_MS);
  const executedDay = Math.floor(runCreatedAtMs / DAY_MS);
  const days = executedDay - boundaryDay;
  if (days < NOTE_AFTER_DAYS) {
    return { level: "silent", days: Math.max(days, 0) };
  }
  return {
    level: days > PROMINENT_AFTER_DAYS ? "prominent" : "note",
    days,
  };
};

/** "(about 7 weeks)" once the day count stops being easy to feel. */
export const weeksPhrase = (days: number): string => {
  const weeks = Math.floor(days / 7);
  return weeks >= 2 ? ` (about ${weeks} weeks)` : "";
};
